/**
 * Uniform-grid spatial hash for 2D proximity queries.
 * Numeric cell keys avoid string alloc; buckets reuse arrays across rebuilds.
 */

export class SpatialHash2D<T> {
  private cellSize = 48;
  private readonly cells = new Map<number, T[]>();
  private readonly freeBuckets: T[][] = [];

  setCellSize(size: number): void {
    this.cellSize = Math.max(1, size);
  }

  getCellSize(): number {
    return this.cellSize;
  }

  clear(): void {
    for (const bucket of this.cells.values()) {
      bucket.length = 0;
      this.freeBuckets.push(bucket);
    }
    this.cells.clear();
  }

  private cellKey(cx: number, cy: number): number {
    // 10-bit lanes (±512 cells) — map sizes with cell≈32–64.
    return ((cx + 512) << 10) | (cy + 512);
  }

  private bucket(cx: number, cy: number): T[] {
    const key = this.cellKey(cx, cy);
    let b = this.cells.get(key);
    if (!b) {
      b = this.freeBuckets.pop() ?? [];
      this.cells.set(key, b);
    }
    return b;
  }

  insert(x: number, y: number, item: T): void {
    const cs = this.cellSize;
    const cx = Math.floor(x / cs);
    const cy = Math.floor(y / cs);
    this.bucket(cx, cy).push(item);
  }

  /** Append candidates whose cells overlap the query disk (may include far items). */
  queryDisk(x: number, y: number, radius: number, out: T[]): void {
    const cs = this.cellSize;
    // Non-finite / huge radii would spin `for (cx=±Inf…)` forever (soft-sep hang).
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(radius)) {
      // Lazy import avoided — callers that care listen via raid-freeze-diag at soft-sep.
      // Keep this path allocation-free: console only when clearly pathological.
      console.warn("[raid-freeze]", "loop_guard:queryDisk_nonfinite", {
        x,
        y,
        radius,
      });
      return;
    }
    const r = Math.max(0, radius);
    let minCx = Math.floor((x - r) / cs);
    let maxCx = Math.floor((x + r) / cs);
    let minCy = Math.floor((y - r) / cs);
    let maxCy = Math.floor((y + r) / cs);
    // Cap span so a bad radius cannot iterate millions of empty cells.
    const maxSpan = 64;
    const spanX = maxCx - minCx;
    const spanY = maxCy - minCy;
    if (spanX > maxSpan || spanY > maxSpan) {
      console.warn("[raid-freeze]", "loop_guard:queryDisk_span_cap", {
        x,
        y,
        radius: r,
        spanX,
        spanY,
        maxSpan,
      });
    }
    if (maxCx - minCx > maxSpan) {
      const mid = Math.floor((minCx + maxCx) / 2);
      minCx = mid - Math.floor(maxSpan / 2);
      maxCx = minCx + maxSpan;
    }
    if (maxCy - minCy > maxSpan) {
      const mid = Math.floor((minCy + maxCy) / 2);
      minCy = mid - Math.floor(maxSpan / 2);
      maxCy = minCy + maxSpan;
    }
    for (let cx = minCx; cx <= maxCx; cx++) {
      for (let cy = minCy; cy <= maxCy; cy++) {
        const b = this.cells.get(this.cellKey(cx, cy));
        if (!b) continue;
        for (let i = 0; i < b.length; i++) out.push(b[i]!);
      }
    }
  }
}
